import cx from 'classnames';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';

import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import { isEmbeddedDocument } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import {
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@trello/nachos/onboarding-experimental';
import { useIsInboxBoardPassContextVals } from '@trello/personal-workspace';
import { useSharedStateSelector } from '@trello/shared-state';
import { useSplitScreenSharedState } from '@trello/split-screen';

import { TrelloBoard } from 'app/src/components/Board';
import { legacyBoardModelsSharedState } from 'app/src/components/Board/legacyBoardModelsSharedState';
import { LazyInboxContainer } from 'app/src/components/Inbox';
import { INBOX_ROOT_ID } from 'app/src/components/Inbox/Inbox.constants';
import { useInboxSocketSubscription } from 'app/src/components/Inbox/useInboxSocketSubscription';
import { usePersonalProductivityCohortValue } from 'app/src/components/PersonalProductivityBeta';
import { PPOnboardingModalContainer } from 'app/src/components/PersonalProductivityBeta/PPOnboardingModal/PPOnboardingModalContainer';
import {
  INBOX_SPOTLIGHT,
  usePersonalProductivitySpotlightTour,
} from 'app/src/components/PersonalProductivityOnboarding';
import { LazyPlanner } from 'app/src/components/Planner';
import { CollapsibleSplitScreenPanel } from 'app/src/components/SplitScreenPanel';
import { PanelNavigation } from './PanelNavigation';
import { RovoIsland } from './RovoIsland';

import styles from './ResizableCollapsibleSplitScreenManager.less';

const SplitScreen: FunctionComponent = () => {
  const { board } = useSharedStateSelector(
    legacyBoardModelsSharedState,
    useCallback((state) => {
      return {
        board: state.board,
      };
    }, []),
  );

  const memberId = useMemberId();
  const boardId = board.model?.get('id') ?? null;
  const workspaceId = board.model?.get('idOrganization') ?? '';
  const isInboxBoard = useIsInboxBoardPassContextVals({
    boardId,
    memberId,
  });
  const screenManagerRef = useRef<HTMLDivElement>(null);

  const {
    ref,
    configuration,
    panels,
    areMultiplePanelsOpen,
    isBoardOpenBlocked,
    toggleBoard,
    toggleInbox,
    togglePlanner,
  } = useSplitScreenSharedState({ isInboxBoard });

  useInboxSocketSubscription();

  const { renderActiveSpotlight } = usePersonalProductivitySpotlightTour({
    source: 'inbox',
    boardId,
  });

  return (
    <div className={styles.manager} ref={ref}>
      <SpotlightManager blanketIsTinted={true}>
        <div
          className={cx({
            [styles.splitscreen]: true,
            [styles.tabs]: configuration === 'tabs',
            [styles.single]: !areMultiplePanelsOpen,
          })}
          ref={screenManagerRef}
        >
          <CollapsibleSplitScreenPanel
            panelName={'inbox'}
            isHidden={!panels.inbox}
          >
            <SpotlightTarget name={INBOX_SPOTLIGHT}>
              <div id={INBOX_ROOT_ID} className={styles.inboxContent}>
                {panels.inbox && <LazyInboxContainer />}
              </div>
            </SpotlightTarget>
            <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>
          </CollapsibleSplitScreenPanel>
          <CollapsibleSplitScreenPanel
            panelName={'planner'}
            isHidden={!panels.planner}
          >
            <div id="trello-planner-root" className={styles.content}>
              {panels.planner && (
                <LazyPlanner screenManagerRef={screenManagerRef} />
              )}
            </div>
          </CollapsibleSplitScreenPanel>
          <CollapsibleSplitScreenPanel
            panelName={'board'}
            isHidden={!panels.board}
          >
            <div id="trello-board-root" className={styles.content}>
              <TrelloBoard />
            </div>
          </CollapsibleSplitScreenPanel>
        </div>

        <PanelNavigation
          {...panels}
          configuration={configuration}
          toggleInbox={toggleInbox}
          togglePlanner={togglePlanner}
          toggleBoard={toggleBoard}
          isBoardOpenBlocked={isBoardOpenBlocked}
        />
        {configuration === 'panels' && <RovoIsland workspaceId={workspaceId} />}
      </SpotlightManager>
    </div>
  );
};

export const ResizableCollapsibleSplitScreenManager: FunctionComponent = () => {
  const { value: isPersonalProdEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  const [isOnboardingOpen, setIsOnboardingOpen] = useState(true);

  const personalProductivityCohort = usePersonalProductivityCohortValue();

  const closeOnboarding = useCallback(() => {
    setIsOnboardingOpen(false);
  }, []);

  const showSplitScreenOnboarding = useMemo(
    () =>
      !isOneTimeMessageDismissed('split-screen-onboarding') &&
      !isOneTimeMessageDismissed('new-user-onboarding') &&
      isOnboardingOpen &&
      personalProductivityCohort !== 'ga',
    [isOneTimeMessageDismissed, isOnboardingOpen, personalProductivityCohort],
  );

  if (!isPersonalProdEnabled || isEmbeddedDocument() || !isMemberLoggedIn()) {
    return <TrelloBoard />;
  }

  if (showSplitScreenOnboarding) {
    return <PPOnboardingModalContainer onComplete={closeOnboarding} />;
  }

  return <SplitScreen />;
};
